// src/components/seances/SeanceMenu.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactPlayer from "react-player";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  List,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Constants from "../Utils/Constants";
import SeanceDetailsModal from "./SeanceDetailsModal";

export default function SeanceMenu() {
  const { t, i18n } = useTranslation("seances", { useSuspense: false });
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: "Bearer " + token,
    "Accept-Language": i18n.language,
  };

  const jsonHeaders = {
    ...authHeaders,
    "Content-Type": "application/json",
  };

  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const [sessionVideoKeys, setSessionVideoKeys] = useState([]);
  const [sessionVideoLoading, setSessionVideoLoading] = useState(false);
  const [videoSearch, setVideoSearch] = useState("");
  const [selectedSessionVideoKey, setSelectedSessionVideoKey] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsSeanceKey, setDetailsSeanceKey] = useState("");

  const openModal = (text, isError = false) => {
    Modal[isError ? "error" : "success"]({
      title: isError
        ? t("label_error") || "Erreur"
        : t("label_success") || "Succès",
      content: text,
    });
  };

  const fetchSessions = () => {
    setFetching(true);

    axios
      .get(`${Constants.SERVER_URL}/sessions`, { headers: authHeaders })
      .then((res) => setSessions(res.data || []))
      .catch((err) =>
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Erreur lors du chargement des séances",
          true
        )
      )
      .finally(() => setFetching(false));
  };

  const fetchSessionVideos = () => {
    setSessionVideoLoading(true);

    axios
      .get(`${Constants.SERVER_URL}/api/minio/session-videos`, {
        headers: authHeaders,
      })
      .then((res) => setSessionVideoKeys(res.data?.keys || []))
      .catch(() => setSessionVideoKeys([]))
      .finally(() => setSessionVideoLoading(false));
  };

  const getSignedVideoUrl = async (key) => {
    const res = await axios.get(
      `${Constants.SERVER_URL}/api/minio/presign-download`,
      {
        headers: authHeaders,
        params: {
          bucket: "rxapa-videos",
          key,
        },
      }
    );

    return res.data.downloadUrl;
  };

  const openVideoPreview = async (key) => {
    if (!key) return;

    try {
      setPreviewLoading(true);
      setPreviewOpen(true);

      if (String(key).startsWith("sessions/")) {
        const url = await getSignedVideoUrl(key);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(key);
      }
    } catch {
      setPreviewUrl(null);
      message.error("Impossible de générer l’aperçu vidéo.");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);

    axios
      .get(`${Constants.SERVER_URL}/sessions`, { headers: authHeaders })
      .then((res) => setSessions(res.data || []))
      .catch((err) =>
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Erreur lors du chargement des séances",
          true
        )
      )
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!createOpen) return;

    fetchSessionVideos();
    setVideoSearch("");
    setSelectedSessionVideoKey(null);
    setPreviewUrl(null);
  }, [createOpen]);

  const onSubmitCreate = () => {
    const data = {
      name: form.name?.trim(),
      description: form.description?.trim(),
      videoUrl: selectedSessionVideoKey?.trim() || "",
    };

    if (!data.name) {
      message.warning(
        t("label_session_name_required") || "Le nom de la séance est requis"
      );
      return;
    }

    if (!data.videoUrl) {
      message.warning("Veuillez sélectionner une vidéo de séance depuis MinIO.");
      return;
    }

    if (!data.videoUrl.startsWith("sessions/")) {
      message.error("Clé vidéo invalide. Elle doit commencer par sessions/.");
      return;
    }

    setCreating(true);

    axios
      .post(`${Constants.SERVER_URL}/create-session`, data, {
        headers: jsonHeaders,
      })
      .then((res) => {
        const sessionKey =
          res?.data?.key ||
          res?.data?.id ||
          res?.data?.sessionKey ||
          res?.data?.session?.key ||
          res?.data?.data?.key;

        message.success(t(`Backend:${res?.data?.message}`) || "Séance créée");

        setCreateOpen(false);
        setForm({ name: "", description: "" });
        setSelectedSessionVideoKey(null);
        setPreviewUrl(null);

        if (sessionKey) {
          navigate(`/seances/${sessionKey}`, { replace: true });
        } else {
          fetchSessions();
        }
      })
      .catch((err) => {
        message.error(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Création impossible"
        );
      })
      .finally(() => setCreating(false));
  };

  const deleteSession = (sessionKey) => {
    axios
      .delete(
        `${Constants.SERVER_URL}/delete-session/${encodeURIComponent(
          sessionKey
        )}`,
        { headers: authHeaders }
      )
      .then((res) => {
        message.success(
          t(`Backend:${res?.data?.message}`) || "Séance supprimée"
        );
        fetchSessions();
      })
      .catch((err) =>
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Suppression impossible",
          true
        )
      );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return sessions;

    return sessions.filter((s) => {
      const name = String(s?.name || "").toLowerCase();
      const desc = String(s?.description || "").toLowerCase();
      const video = String(s?.videoUrl || "").toLowerCase();
      const key = String(s?.key || "").toLowerCase();

      return (
        name.includes(q) ||
        desc.includes(q) ||
        video.includes(q) ||
        key.includes(q)
      );
    });
  }, [sessions, search]);

  const filteredSessionVideos = useMemo(() => {
    const q = videoSearch.trim().toLowerCase();

    if (!q) return sessionVideoKeys;

    return sessionVideoKeys.filter((k) =>
      String(k).toLowerCase().includes(q)
    );
  }, [sessionVideoKeys, videoSearch]);

  return (
    <div
      style={{
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Space
        align="center"
        style={{ justifyContent: "space-between", width: "100%" }}
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("label_sessions_title") || "Séances"}
        </Typography.Title>

        <Space wrap>
          <Segmented
            value={view}
            onChange={setView}
            options={[
              {
                value: "grid",
                icon: <AppstoreOutlined />,
                label: t("label_grid") || "Grille",
              },
              {
                value: "list",
                icon: <UnorderedListOutlined />,
                label: t("label_list") || "Liste",
              },
            ]}
          />

          <Tooltip title={t("label_refresh") || "Rafraîchir"}>
            <Button
              icon={<ReloadOutlined spin={fetching} />}
              onClick={fetchSessions}
            />
          </Tooltip>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            {t("label_new_session") || "Nouvelle séance"}
          </Button>
        </Space>
      </Space>

      <Space>
        <Input.Search
          allowClear
          placeholder={t("label_search_session") || "Rechercher…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        {fetching && <Tag>MAJ…</Tag>}
      </Space>

      <div style={{ minHeight: 240 }}>
        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 48 }}
          >
            <Spin
              tip={t("label_loading_sessions") || "Chargement des séances…"}
            />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description={t("label_no_sessions") || "Aucune séance."} />
        ) : view === "grid" ? (
          <Row gutter={[16, 16]}>
            {filtered.map((s) => (
              <Col key={s.key} xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card
                  hoverable
                  title={
                    <Typography.Text strong ellipsis style={{ maxWidth: 200 }}>
                      {s.name || "Sans nom"}
                    </Typography.Text>
                  }
                  actions={[
                    <Tooltip title={t("label_edit") || "Éditer"} key="edit">
                      <EditOutlined
                        onClick={() => navigate(`/seances/${s.key}`)}
                      />
                    </Tooltip>,

                    <Tooltip title={t("label_view") || "Voir"} key="view">
                      <EyeOutlined
                        onClick={() => {
                          setIsDetailsOpen(true);
                          setDetailsSeanceKey(s.key);
                        }}
                      />
                    </Tooltip>,

                    <Popconfirm
                      title={
                        t("label_confirm_delete") ||
                        "Supprimer cette séance ?"
                      }
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteSession(s.key)}
                      key="delete"
                    >
                      <DeleteOutlined style={{ color: "#ff4d4f" }} />
                    </Popconfirm>,
                  ]}
                >
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Typography.Paragraph ellipsis={{ rows: 2 }}>
                      {s.description || "—"}
                    </Typography.Paragraph>

                    {s.videoUrl && (
                      <Typography.Text type="secondary" ellipsis>
                        {String(s.videoUrl)}
                      </Typography.Text>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {filtered.map((s) => (
              <Card key={s.key} size="small">
                <Space
                  align="start"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>
                      {s.name || "Sans nom"}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {s.description || "—"}
                    </Typography.Text>
                    {s.videoUrl && (
                      <Typography.Text type="secondary">
                        {String(s.videoUrl)}
                      </Typography.Text>
                    )}
                  </Space>

                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/seances/${s.key}`)}
                    >
                      {t("label_edit") || "Éditer"}
                    </Button>

                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setIsDetailsOpen(true);
                        setDetailsSeanceKey(s.key);
                      }}
                    >
                      {t("label_view") || "Voir"}
                    </Button>

                    <Popconfirm
                      title={
                        t("label_confirm_delete") ||
                        "Supprimer cette séance ?"
                      }
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteSession(s.key)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        {t("label_delete") || "Supprimer"}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <SeanceDetailsModal
        open={isDetailsOpen}
        seanceKey={detailsSeanceKey}
        onClose={() => setIsDetailsOpen(false)}
      />

      <Modal
        title={t("label_create_session_title") || "Créer une séance"}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText={t("label_create") || "Créer"}
        okButtonProps={{ loading: creating }}
        onOk={onSubmitCreate}
        destroyOnClose
        width={760}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            placeholder={t("label_session_name") || "Nom de la séance"}
            value={form.name}
            onChange={(e) =>
              setForm((s) => ({ ...s, name: e.target.value }))
            }
            maxLength={80}
          />

          <Input.TextArea
            placeholder={
              t("label_session_description") || "Description (optionnel)"
            }
            value={form.description}
            onChange={(e) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={500}
          />

          <div style={{ marginTop: 8 }}>
            <Typography.Text strong>Vidéo de séance</Typography.Text>

            <Input
              style={{ marginTop: 8 }}
              placeholder="Rechercher une vidéo…"
              value={videoSearch}
              onChange={(e) => setVideoSearch(e.target.value)}
              allowClear
            />

            <div style={{ marginTop: 8 }}>
              {sessionVideoLoading ? (
                <div style={{ padding: 12 }}>
                  <Spin />
                </div>
              ) : (
                <List
                  size="small"
                  bordered
                  style={{ maxHeight: 260, overflow: "auto" }}
                  dataSource={filteredSessionVideos}
                  locale={{ emptyText: "Aucune vidéo trouvée dans MinIO." }}
                  renderItem={(k) => {
                    const selected = k === selectedSessionVideoKey;

                    return (
                      <List.Item
                        onClick={() => setSelectedSessionVideoKey(k)}
                        style={{
                          cursor: "pointer",
                          background: selected
                            ? "rgba(22,119,255,0.08)"
                            : "transparent",
                        }}
                        actions={[
                          <Button
                            key="preview"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openVideoPreview(k);
                            }}
                          >
                            Aperçu
                          </Button>,

                          selected ? (
                            <Tag key="selected" color="blue">
                              Sélectionné
                            </Tag>
                          ) : null,
                        ]}
                      >
                        <Typography.Text ellipsis style={{ maxWidth: 460 }}>
                          {k}
                        </Typography.Text>
                      </List.Item>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </Space>
      </Modal>

      <Modal
        open={previewOpen}
        onCancel={() => {
          setPreviewOpen(false);
          setPreviewUrl(null);
        }}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {previewLoading ? (
          <Spin />
        ) : previewUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={previewUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="Impossible d’afficher la vidéo." />
        )}
      </Modal>
    </div>
  );
}